import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import { Check, ExternalLink, Plus, Sparkles, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { getCandidate } from '@/lib/api'
import { Avatar } from '@/lib/atsUi'

// A candidate to preview in the drawer. The AI-screening block (verdict/score/rationale) is optional —
// the Live Screening page passes it; the Candidates list omits it for a plain profile overview.
export interface DrawerCandidate {
  candidateId: string
  name: string | null
  employer?: string | null
  totalMonthsExp: number | null
  location: string | null
  verdict?: 'strong' | 'maybe' | 'weak' | null
  score?: number
  confidence?: string | null
  rationale?: string
  gaps?: string[]
  strengths?: string[]
  concerns?: string[]
  pointsToDiscuss?: string[]
}

const fmtExp = (m: number | null | undefined) => (m == null ? '—' : `${Math.floor(m / 12)}y ${m % 12}m`)
const verdictVariant = (v: string | null | undefined): 'success' | 'warning' | 'error' | 'info' =>
  v === 'strong' ? 'success' : v === 'weak' ? 'error' : v === 'maybe' ? 'warning' : 'info'

// The fit score as a prominent colored circle — green=strong, amber=maybe, red=weak.
function ScoreCircle({ score, verdict }: { score?: number; verdict?: string | null }) {
  const tone = verdict === 'strong' ? 'bg-success' : verdict === 'weak' ? 'bg-error' : verdict === 'maybe' ? 'bg-warning' : 'bg-muted-foreground'
  return (
    <span className={`flex h-11 w-11 items-center justify-center rounded-full text-base font-bold tabular-nums text-white shadow-sm ${tone}`}>
      {score ?? '—'}
    </span>
  )
}

// Render verdict text with ONLY the **key values** highlighted (not the whole sentence).
function HL({ text, tone }: { text: string; tone: 'good' | 'bad' | 'neutral' }) {
  const cls = tone === 'good' ? 'bg-success/25' : tone === 'bad' ? 'bg-orange-400/30' : 'bg-info/20'
  return (
    <>
      {text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
        p.startsWith('**') && p.endsWith('**')
          ? <span key={i} className={`rounded px-0.5 font-medium ${cls}`}>{p.slice(2, -2)}</span>
          : <span key={i}>{p}</span>,
      )}
    </>
  )
}

function skillsOf(pj: unknown): string[] {
  const p = pj as { skills?: unknown; skillsetRaw?: unknown } | null
  if (Array.isArray(p?.skills) && p.skills.length) return p.skills.map(String)
  if (typeof p?.skillsetRaw === 'string') return p.skillsetRaw.split(',').map((s) => s.trim()).filter(Boolean)
  return []
}

export function CandidateDrawer({ cand, onClose, selected, onToggleSelect }: { cand: DrawerCandidate | null; onClose: () => void; selected?: boolean; onToggleSelect?: () => void }) {
  return (
    <AnimatePresence>
      {cand && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/30"
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.22 }}
            className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col overflow-y-auto border-l border-border bg-background shadow-xl"
          >
            <div className="flex items-center justify-between border-b border-border px-5 py-3">
              <span className="text-sm font-medium">Candidate profile</span>
              <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </div>
            <CandidateDetail cand={cand} selected={selected} onToggleSelect={onToggleSelect} />
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}

// The candidate scorecard + skills + contact + shortlist action. Reused by the slide-over drawer AND the
// full-page comparison view, so both render identically.
export function CandidateDetail({ cand, selected, onToggleSelect }: { cand: DrawerCandidate; selected?: boolean; onToggleSelect?: () => void }) {
  const { data, isLoading } = useQuery({ queryKey: ['candidate', cand.candidateId], queryFn: () => getCandidate(cand.candidateId) })
  const c = data && 'candidate' in data ? data.candidate : null
  const skills = skillsOf(c?.parsedJson)
  const hasScreening = cand.score != null
  return (
    <div className="space-y-4 p-5">
              {/* Identity + the prominent shortlist action on the SAME row */}
              <div className="flex items-center gap-3">
                <Avatar name={cand.name ?? '?'} className="h-11 w-11 shrink-0 text-sm" />
                <div className="min-w-0 flex-1">
                  <div className="text-base font-semibold text-foreground">{cand.name ?? '—'}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {(c?.employer ?? cand.employer) ?? '—'} · {fmtExp(cand.totalMonthsExp ?? c?.totalMonthsExp)} · {cand.location ?? c?.location ?? '—'}
                  </div>
                </div>
                {onToggleSelect && (
                  <button
                    onClick={onToggleSelect}
                    className={`inline-flex shrink-0 items-center gap-1.5 rounded-md px-3.5 py-2 text-sm font-semibold transition-colors ${selected ? 'bg-success/15 text-success ring-1 ring-inset ring-success/40 hover:bg-success/20' : 'bg-primary text-primary-foreground shadow-sm hover:opacity-90'}`}
                  >
                    {selected ? <><Check className="h-4 w-4" /> Shortlisted</> : <><Plus className="h-4 w-4" /> Shortlist</>}
                  </button>
                )}
              </div>

              {hasScreening && (
                <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
                  <div className="mb-2 flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">AI screening</span>
                    <Badge variant={verdictVariant(cand.verdict)}>{cand.verdict ?? 'scored'}</Badge>
                    {cand.confidence && <span className="rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] text-muted-foreground">{cand.confidence} confidence</span>}
                    <span className="ml-auto"><ScoreCircle score={cand.score} verdict={cand.verdict} /></span>
                  </div>
                  {cand.rationale && <p className="text-sm text-foreground"><HL text={cand.rationale} tone="neutral" /></p>}
                  {cand.strengths && cand.strengths.length > 0 && (
                    <div className="mt-3">
                      <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-success">Strengths</div>
                      <ul className="space-y-1">{cand.strengths.map((s, i) => <li key={i} className="flex gap-1.5 text-xs"><Check className="mt-0.5 h-3 w-3 shrink-0 text-success" /><span className="text-foreground"><HL text={s} tone="good" /></span></li>)}</ul>
                    </div>
                  )}
                  {cand.concerns && cand.concerns.length > 0 && (
                    <div className="mt-3 border-t border-border/60 pt-3">
                      <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-orange-600">Concerns to weigh</div>
                      <ul className="space-y-1">{cand.concerns.map((s, i) => <li key={i} className="flex gap-1.5 text-xs"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500" /><span className="text-foreground"><HL text={s} tone="bad" /></span></li>)}</ul>
                    </div>
                  )}
                  {cand.pointsToDiscuss && cand.pointsToDiscuss.length > 0 && (
                    <div className="mt-3 border-t border-border/60 pt-3">
                      <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Points to discuss</div>
                      <ul className="space-y-1">{cand.pointsToDiscuss.map((s, i) => <li key={i} className="flex gap-1.5 text-xs text-muted-foreground"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/50" /><span><HL text={s} tone="neutral" /></span></li>)}</ul>
                    </div>
                  )}
                  {cand.gaps && cand.gaps.length > 0 && <p className="mt-2 text-xs text-error">Missing must-haves: {cand.gaps.join(', ')}</p>}
                </div>
              )}

              <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
                <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Skills</div>
                {isLoading ? (
                  <span className="text-sm text-muted-foreground">Loading…</span>
                ) : skills.length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {skills.map((s) => (
                      <span key={s} className="inline-flex items-center rounded border border-border bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground">
                        {s}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </div>

              <div className="space-y-2 rounded-lg border border-border bg-card p-4 text-sm shadow-sm">
                <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Contact & fitment</div>
                <Field label="Email" value={c?.email} />
                <Field label="Phone" value={c?.phone} />
                <Field label="Current CTC" value={c?.currentCtc} />
                <Field label="Expected CTC" value={c?.expectedCtc} />
                <Field label="Notice" value={c?.noticeDays != null ? `${c.noticeDays} days` : null} />
                <Field label="Preferred" value={c?.preferredLocations?.join(', ')} />
              </div>

              <Link
                to="/candidates/$candidateId"
                params={{ candidateId: cand.candidateId }}
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                Open full profile <ExternalLink className="h-3.5 w-3.5" />
              </Link>
            </div>
  )
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium text-foreground">{value || '—'}</span>
    </div>
  )
}
